/**
 * useChat.ts
 * 
 * Manages the AI chat state, messages history, and editing suggestions.
 */

import { useState, useCallback } from 'react';
import { ChatMessage, EditingSuggestion } from '../types';

export const useChat = () => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [editingSuggestions, setEditingSuggestions] = useState<EditingSuggestion[]>([]);

    // Helper to add a user message
    const addUserMessage = useCallback((text: string) => {
        const newMessage: ChatMessage = {
            id: Date.now(),
            role: 'user',
            text
        };
        setMessages(prev => [...prev, newMessage]);
        return newMessage;
    }, []);

    // Helper to add an assistant message (loading state)
    const addAssistantMessage = useCallback((text: string = '', isLoading: boolean = true) => {
        const id = Date.now() + 1; // Ensure unique ID even if fast
        const newMessage: ChatMessage = {
            id,
            role: 'assistant',
            text,
            isLoading
        };
        setMessages(prev => [...prev, newMessage]);
        return id; // Return ID to allow updates
    }, []);

    // Helper to update a specific message
    const updateMessage = useCallback((id: number | string, updates: Partial<ChatMessage>) => {
        setMessages(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));
    }, []);

    return {
        messages,
        setMessages,
        editingSuggestions,
        setEditingSuggestions,
        addUserMessage,
        addAssistantMessage,
        updateMessage
    };
};
